import { useState, useMemo } from 'react';
import { Strategy } from '../types';
import { useToast } from '@/components/ui/use-toast';

interface UseStrategyManagementProps {
  strategies: Strategy[];
  selectedStrategy: string;
  setSelectedStrategy: (strategy: string) => void;
  onNewStrategy: (data: any) => void;
}

export const useStrategyManagement = ({
  strategies,
  selectedStrategy,
  setSelectedStrategy,
  onNewStrategy
}: UseStrategyManagementProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStrategyDetails, setSelectedStrategyDetails] = useState<Strategy | null>(null);
  const { toast } = useToast();

  const { displayableActiveStrategies, displayableInactiveStrategies } = useMemo(() => {
    // Less restrictive filtering to ensure strategies are displayed
    // Show strategies even if backtestSettings.mode isn't explicitly set
    // or if isActive isn't explicitly set
    
    // In production, force all strategies to be displayable regardless of mode or active status
    // This is a temporary bypass to ensure all strategies show up
    const allDisplayableStrategies = strategies;

    // For debugging purposes, log all strategies that don't meet the typical criteria
    const nonStandardStrategies = strategies.filter(s => {
      const hasValidMode = s.backtestSettings?.mode === 'live' || s.backtestSettings?.mode === 'paper';
      const isActiveTrue = s.isActive === true;
      return !hasValidMode && !isActiveTrue;
    });

    if (nonStandardStrategies.length > 0) {
      console.log('Strategies not meeting standard display criteria:', nonStandardStrategies);
    }

    // Then filter based on isActive status for display separation
    // Be extremely permissive - any strategy without isActive=false is considered active
    const active = allDisplayableStrategies.filter(s => s.isActive !== false); 
    const inactive = allDisplayableStrategies.filter(s => s.isActive === false);
    
    return {
      displayableActiveStrategies: active,
      displayableInactiveStrategies: inactive
    };
  }, [strategies]);

  const handleResearch = (strategyId: string) => {
    toast({
      title: "Researching Strategy",
      description: "Opening Financial Analyst chat...",
    });
  };

  const handleAddStrategy = () => {
    setShowAddDialog(true);
  };

  const handleDeleteClick = (strategyId: string) => {
    setSelectedStrategy(strategyId);
    setShowDeleteDialog(true);
  };

  const handleStrategySubmit = (data: {
    name: string;
    engine: string;
    riskLevel: string;
    duration: string;
    symbols: string;
    profitTarget: string;
    stopLoss: string;
    isDynamicRisk: boolean;
  }) => {
    onNewStrategy(data);
    setShowAddDialog(false);
    toast({
      title: "Strategy Created",
      description: `Created strategy: ${data.name}`,
    });
  };

  return {
    isExpanded,
    setIsExpanded,
    showAddDialog,
    setShowAddDialog,
    showDeleteDialog,
    setShowDeleteDialog,
    selectedStrategyDetails,
    setSelectedStrategyDetails,
    displayableActiveStrategies,
    displayableInactiveStrategies,
    handleResearch,
    handleAddStrategy,
    handleDeleteClick,
    handleStrategySubmit
  };
};
